import { create } from 'zustand';
import type { Alert, AlertHistory, CreateAlertPayload } from '@/types/alerts';
import { useChartStore } from '@/store/chartStore';
import { api } from '@/services/api';

const PLAN_ALERT_LIMITS: Record<string, number> = {
  basic:    2,
  pro:      100,
  business: 100,
};

export function getAlertLimit(plan: string): number {
  return PLAN_ALERT_LIMITS[plan] ?? 2;
}

interface AlertState {
  alerts:         Alert[];
  history:        AlertHistory[];
  loading:        boolean;
  error:          string | null;
  hiddenAlertIds: Set<string>;

  // Modal state
  createModalOpen: boolean;
  editingAlert:    Alert | null;
  prefillPrice:    number | null;
  prefillSymbol:   string | null;

  // Actions
  fetchAlerts:          (symbol?: string) => Promise<void>;
  fetchHistory:         (symbol?: string) => Promise<void>;
  createAlert:          (payload: CreateAlertPayload) => Promise<{ id: string } | null>;
  updateAlert:          (id: string, payload: Partial<CreateAlertPayload> & { active?: boolean }) => Promise<boolean>;
  deleteAlert:          (id: string) => Promise<boolean>;
  toggleAlertVisibility: (id: string) => void;

  exportAlerts:  (symbol?: string) => Promise<void>;
  importAlerts:  (data: string, force?: boolean) => Promise<ImportResult>;

  // Live bars (100 daily candles) keyed by symbol — refreshed after any alert mutation
  alertBars:      Map<string, BarData[]>;
  fetchAlertBars: () => Promise<void>;

  openCreateModal: (prefillPrice?: number, prefillSymbol?: string) => void;
  openEditModal:   (alert: Alert) => void;
  closeModal:      () => void;
  resetForLogout:  () => void;
}

export interface BarData { close: number; volume?: number; time?: number; open?: number; high?: number; low?: number }

export interface ImportResult {
  created:      number;
  skipped:      number;
  limitReached: boolean;
  duplicates?:  Array<{ symbol: string; name: string | null; conditionLogic: string; conditions: unknown[] }>;
}

export const useAlertStore = create<AlertState>((set, get) => ({
  alerts:          [],
  history:         [],
  loading:         false,
  error:           null,
  hiddenAlertIds:  new Set<string>(),
  createModalOpen: false,
  editingAlert:    null,
  prefillPrice:    null,
  prefillSymbol:   null,
  alertBars:       new Map(),

  toggleAlertVisibility: (id) => {
    const hidden = new Set(get().hiddenAlertIds);
    if (hidden.has(id)) hidden.delete(id); else hidden.add(id);
    set({ hiddenAlertIds: hidden });
  },

  openCreateModal: (prefillPrice, prefillSymbol) => {
    // Switch to the alerts panel so the modal appears in context
    const { rightPanelActive, setRightPanelActive, rightToolbarOpen, toggleRightToolbar } = useChartStore.getState();
    if (rightPanelActive !== 'alerts') {
      setRightPanelActive('alerts');
      if (!rightToolbarOpen) toggleRightToolbar();
    }
    set({
      createModalOpen: true,
      editingAlert:    null,
      prefillPrice:    prefillPrice ?? null,
      prefillSymbol:   prefillSymbol ?? null,
    });
  },

  openEditModal: (alert) => set({
    createModalOpen: true,
    editingAlert:    alert,
    prefillPrice:    null,
    prefillSymbol:   null,
  }),

  closeModal: () => set({
    createModalOpen: false,
    editingAlert:    null,
    prefillPrice:    null,
    prefillSymbol:   null,
  }),

  resetForLogout: () => set({
    alerts:          [],
    history:         [],
    loading:         false,
    error:           null,
    hiddenAlertIds:  new Set<string>(),
    createModalOpen: false,
    editingAlert:    null,
    prefillPrice:    null,
    prefillSymbol:   null,
    alertBars:       new Map(),
  }),

  fetchAlertBars: async () => {
    const { alerts } = get();
    const activeSymbols = [...new Set(
      alerts.filter((a) => a.active && !a.triggered).map((a) => a.symbol)
    )];
    if (activeSymbols.length === 0) { set({ alertBars: new Map() }); return; }
    // ohlcv '1D' already appends today's live bar (from Analytics Engine) during
    // market hours — no separate intraday fetch needed.
    const results = await Promise.allSettled(
      activeSymbols.map((sym) =>
        api.ohlcv(sym, '1D', 100).then((r) => ({ sym, bars: r.data as BarData[] }))
      ),
    );
    const map = new Map<string, BarData[]>();
    for (const r of results) {
      if (r.status === 'fulfilled') map.set(r.value.sym, r.value.bars);
    }
    set({ alertBars: map });
  },

  fetchAlerts: async (symbol) => {
    set({ loading: true, error: null });
    try {
      const url = symbol
        ? `/api/user/alerts?symbol=${encodeURIComponent(symbol)}`
        : '/api/user/alerts';
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { alerts } = (await res.json()) as { alerts: Alert[] };
      set({ alerts, loading: false });
      void get().fetchAlertBars();
    } catch (err) {
      set({ error: String(err), loading: false });
    }
  },

  fetchHistory: async (symbol) => {
    try {
      const url = symbol
        ? `/api/user/alerts/history?symbol=${encodeURIComponent(symbol)}`
        : '/api/user/alerts/history';
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return;
      const { history } = (await res.json()) as { history: AlertHistory[] };
      set({ history });
    } catch {
      // Non-critical — history load failure is silent
    }
  },

  createAlert: async (payload) => {
    try {
      const res = await fetch('/api/user/alerts', {
        method:      'POST',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        set({ error: err.error ?? 'Failed to create alert' });
        return null;
      }
      const { id, symbol } = (await res.json()) as { id: string; symbol: string };
      await get().fetchAlerts();
      return { id, symbol } as { id: string };
    } catch (err) {
      set({ error: String(err) });
      return null;
    }
  },

  updateAlert: async (id, payload) => {
    try {
      const res = await fetch(`/api/user/alerts/${id}`, {
        method:      'PUT',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify(payload),
      });
      if (!res.ok) return false;
      await get().fetchAlerts();
      return true;
    } catch {
      return false;
    }
  },

  deleteAlert: async (id) => {
    try {
      const res = await fetch(`/api/user/alerts/${id}`, {
        method:      'DELETE',
        credentials: 'include',
      });
      if (!res.ok) return false;
      set((s) => ({ alerts: s.alerts.filter((a) => a.id !== id) }));
      void get().fetchAlertBars();
      return true;
    } catch {
      return false;
    }
  },

  exportAlerts: async (symbol) => {
    const url = symbol
      ? `/api/user/alerts/export?symbol=${encodeURIComponent(symbol)}`
      : '/api/user/alerts/export';
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
    const { data, filename } = (await res.json()) as { data: string; filename: string };
    // Trigger browser download
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  },

  importAlerts: async (data, force = false) => {
    const res = await fetch('/api/user/alerts/import', {
      method:      'POST',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ data, force }),
    });
    const body = (await res.json()) as ImportResult & { error?: string; duplicates?: ImportResult['duplicates'] };
    if (res.status === 409) {
      // Return duplicates list for the UI to show a confirmation dialog
      return { created: 0, skipped: 0, limitReached: false, duplicates: body.duplicates };
    }
    if (!res.ok) throw new Error(body.error ?? `Import failed: HTTP ${res.status}`);
    await get().fetchAlerts();
    return { created: body.created, skipped: body.skipped, limitReached: body.limitReached ?? false };
  },
}));
